import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import DefaultApiLimit from '../../../../../config/defaultLimit';
import { PortalType } from '../../../../../config/enum';
import { usePortal } from '../../../../../contexts/PortalContext/PortalContext';
import {
  RequestTransactionsInterFace,
  TransactionsInterface,
} from '../../../../../interface/transactions-location-interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import { setSidePanelData } from '../../../../../redux/slices/TableSidePanelSlice';
import {
  disableTransactionFiltersApplied,
  fetchTransactions,
  fetchTransactionsFilters,
  updateTransactionsFilters,
  setSortData,
} from '../../../../../redux/slices/TransactionsLocationSlice';
import CustomDateRangePicker from '../../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import CpoCdrSidePanel from '../../../../../shared-components/table-side-panel/CpoCdrSidePanel';
import DeviceSidePanel from '../../../../../shared-components/table-side-panel/DeviceSidePanel';
import FacilityDetailsSidePanel from '../../../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../../shared-components/table-side-panel/LocationSidePanel';
// import TariffSidePanel from '../../../../../shared-components/table-side-panel/TariffSidePanel';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween } from '../../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../../shared-functions/ExportFile';
// import FormatDuration from '../../../../../shared-functions/FormatDuration';
import { permissionCheck } from '../../../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import cpoTransactionApi from '../../../../../utils/api/cpo-transaction';

/**
 * Formats total time in hours into a human-readable duration string.
 * @param {number | string | undefined | null} totalTime - The total time value in hours.
 * @returns {string} The formatted duration string.
 */
const formatTotalTime = (totalTime: number | string | undefined | null, t: any): string => {
  if (totalTime == null || Number.isNaN(Number(totalTime))) {
    return '0';
  }

  const totalSeconds = Math.round(Number(totalTime) * 3600);
  if (totalSeconds <= 0) {
    return '0';
  }

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const displayTime = [
    days ? `${days} ${days > 1 ? t('days') : t('day')}` : '',
    hours ? `${hours} ${hours > 1 ? t('hrs') : t('hr')}` : '',
    minutes ? `${minutes} ${t('min')}` : '',
    seconds ? `${seconds} ${t('sec')}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return displayTime || '0';
};

/**
 * Station details transaction tab component.
 * @returns {React.JSX.Element} The rendered Transactions component.
 */
function Transactions() {
  const { t, i18n } = useTranslation(['transaction', 'sidePanel']);
  const dispatch = useAppDispatch();
  const { portalType } = usePortal();
  const isPartnerPortal = portalType === PortalType.PARTNER;
  const { selectedChargingPoint } = useAppSelector((state) => state.locationManagement);
  const stationId = selectedChargingPoint?.id;
  const {
    transactions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.transactions);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
        />
      </div>
    );
  };

  /**
   * Fetch transaction list data.
   * @param {RequestTransactionsInterFace} data - The request data payload.
   */
  const fetchData = (data: RequestTransactionsInterFace) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading && stationId) {
      dispatch(
        fetchTransactions({
          ...data,
          stationId: [stationId],
        }),
      );
    }
  };

  /**
   * Function to refresh data.
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
    fetchData({
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      ...sortValue,
    });
  };

  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent } =
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: transactions,
    });

  const column = [
    {
      label: t('CDR ID'),
      accessorKey: 'cdrId' as keyof TransactionsInterface,
      queryParamsKey: 'transactionObjectId',
      queryParamsValue: '_id',
      sidePanelComponent: <CpoCdrSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('Session ID'),
      accessorKey: 'sessionId' as keyof TransactionsInterface,
      queryParamsKey: 'transactionObjectId',
    },
    {
      label: t('OCPPTransactionid'),
      accessorKey: 'transactionId' as keyof TransactionsInterface,
    },
    {
      label: t('Account ID'),
      accessorKey: 'accountCustomId' as keyof TransactionsInterface,
      sortAllow: true,
      ...(!isPartnerPortal
        ? {
            queryParamsKey: 'accountCustomId',
            queryParamsValue: 'accountCustomId',
            sidePanelComponent: (
              <FacilityDetailsSidePanel
                actionActive
                isEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                fetchIdFrom="queryParams"
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                }}
                onRefreshList={handleRefresh}
              />
            ),
            disableNewTab: true,
          }
        : {}),
    },
    {
      label: t('Charging Point'),
      accessorKey: 'chargingPointSn' as keyof TransactionsInterface,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('Location ID'),
      accessorKey: 'cdrLocation' as keyof TransactionsInterface,
      sortAllow: false,
      queryParamsKey: 'stationId',
      queryParamsValue: 'id',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * Location ID custom formatter.
       * @param {TransactionsInterface} row - Row data.
       * @returns {React.JSX.Element} Location ID element.
       */
      customData: (row: TransactionsInterface) => (
        <span
          className="table-row-each-data hyperlink"
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
            if (!row.cdrLocation?.id) return;
            const parameters = new URLSearchParams(searchParameters);
            const sidePanelKeys = [
              'accountCustomId',
              'deviceId',
              'connectorDeviceId',
              'stationId',
              'facilityId',
              'userCustomId',
              'userId',
              'utilityId',
              'transactionObjectId',
              'roleId',
              'sessionId',
              'cdrObjectId',
              'paymentDetailsId',
              'personalChargerId',
              'action',
            ];
            sidePanelKeys.forEach((key) => parameters.delete(key));
            parameters.set('stationId', row.cdrLocation.id);
            setSearchParameters(parameters);
            dispatch(
              setSidePanelData({
                key: 'Transactions',
                value: row.id,
              }),
            );
          }}>
          {row.cdrLocation?.id || '-'}
        </span>
      ),
    },
    {
      label: t('EVSE UID'),
      accessorKey: 'evseUid' as keyof TransactionsInterface,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * EVSE UID custom formatter.
       * @param {TransactionsInterface} row - Row data.
       * @returns {React.JSX.Element} EVSE UID element.
       */
      customData: (row: TransactionsInterface) => (
        <span
          className="table-row-each-data hyperlink"
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
            if (!row.chargingPointSn) return;
            const parameters = new URLSearchParams(searchParameters);
            const sidePanelKeys = [
              'accountCustomId',
              'deviceId',
              'connectorDeviceId',
              'stationId',
              'facilityId',
              'userCustomId',
              'userId',
              'utilityId',
              'transactionObjectId',
              'roleId',
              'sessionId',
              'cdrObjectId',
              'paymentDetailsId',
              'personalChargerId',
              'action',
            ];
            sidePanelKeys.forEach((key) => parameters.delete(key));
            parameters.set('deviceId', row.chargingPointSn);
            setSearchParameters(parameters);
            dispatch(
              setSidePanelData({
                key: 'Transactions',
                value: row.id,
              }),
            );
          }}>
          {row.cdrLocation?.evseUid || '-'}
        </span>
      ),
    },
    {
      label: t('connectorId'),
      accessorKey: 'connectorId' as keyof TransactionsInterface,
      /**
       * Connector ID custom formatter.
       * @param {TransactionsInterface} row - Row data.
       * @returns {React.JSX.Element} Connector ID element.
       */
      customData: (row: TransactionsInterface) => (
        <span>{row.cdrLocation?.connectorId || '-'}</span>
      ),
    },
    // {
    //   label: t('tariffName'),
    //   accessorKey: 'tariffs' as keyof TransactionsInterface,
    //   sortAllow: false,
    //   queryParamsKey: 'tariffId',
    //   sidePanelComponent: <TariffSidePanel fetchIdFrom="queryParams" isPartner={false} />,
    //   disableNewTab: true,
    //   /**
    //    * Tariff name custom formatter.
    //    * @param {TransactionsInterface} row - Row data.
    //    * @returns {React.JSX.Element} Tariff name element.
    //    */
    //   customData: (row: TransactionsInterface) => {
    //     const tariff = row.tariffs?.[0];
    //     const tariffId = tariff?.tariffId || tariff?._id;
    //     const tariffName = tariff?.name || '-';

    //     if (!tariffId || tariffName === '-') {
    //       return <span>{tariffName}</span>;
    //     }

    //     return (
    //       <span
    //         className="table-row-each-data hyperlink"
    //         role="presentation"
    //         onClick={(event) => {
    //           event.stopPropagation();
    //           const parameters = new URLSearchParams(searchParameters);
    //           const sidePanelKeys = [
    //             'accountCustomId',
    //             'deviceId',
    //             'connectorDeviceId',
    //             'stationId',
    //             'facilityId',
    //             'userCustomId',
    //             'userId',
    //             'utilityId',
    //             'transactionObjectId',
    //             'roleId',
    //             'sessionId',
    //             'cdrObjectId',
    //             'paymentDetailsId',
    //             'personalChargerId',
    //             'tariffId',
    //             'action',
    //           ];
    //           sidePanelKeys.forEach((key) => parameters.delete(key));
    //           parameters.set('tariffId', tariffId);
    //           setSearchParameters(parameters);
    //           dispatch(
    //             setSidePanelData({
    //               key: 'Transactions',
    //               value: row.id,
    //             }),
    //           );
    //         }}>
    //         {tariffName}
    //       </span>
    //     );
    //   },
    // },
    {
      label: t('tariffName'),
      accessorKey: 'tariffs' as keyof TransactionsInterface,
      sortAllow: false,
      /**
       * Tariff name custom formatter.
       * @param {TransactionsInterface} row - Row data.
       * @returns {React.JSX.Element} Tariff name element.
       */
      customData: (row: TransactionsInterface) => <span>{row.tariffs?.[0]?.name || '-'}</span>,
    },
    {
      label: t('Energy (kWh)'),
      accessorKey: 'totalEnergy' as keyof TransactionsInterface,
      /**
       * Energy custom formatter.
       * @param {TransactionsInterface} row - Row data.
       * @returns {React.JSX.Element} Energy element.
       */
      customData: (row: TransactionsInterface) => (
        <span>{ToFixedValue(row.totalEnergy, 3) || '0.000'} kWh</span>
      ),
    },
    {
      label: t('Total Cost'),
      accessorKey: 'totalCost' as keyof TransactionsInterface,
      /**
       * Total cost custom formatter.
       * @param {TransactionsInterface} row - Row data.
       * @returns {React.JSX.Element} Formatted total cost element.
       */
      customData: (row: TransactionsInterface) => (
        <span>
          {row.totalCost?.inclVat !== undefined ? ToFixedValue(row.totalCost.inclVat) : '0.00'}{' '}
          {row.currency}
        </span>
      ),
    },
    {
      label: t('Total Time'),
      accessorKey: 'totalTime' as keyof TransactionsInterface,
      /**
       * Render duration in formatted string.
       * @param {TransactionsInterface} rowData - The row data.
       * @returns {React.JSX.Element} The total duration element.
       */
      customData: (rowData: TransactionsInterface) => (
        <span>{formatTotalTime(Number(rowData.totalTime), t)}</span>
      ),
    },
    {
      label: t('Created'),
      accessorKey: 'createdAt' as keyof TransactionsInterface,
      /**
       * Render creation date and time.
       * @param {TransactionsInterface} rowData - The row data.
       * @returns {React.JSX.Element} The formatted date time element.
       */
      customData: (rowData: TransactionsInterface) => (
        <span>{ConvertUTCtoDateTime(rowData.createdAt)}</span>
      ),
    },
    {
      label: t('Last Updated'),
      accessorKey: 'updatedAt' as keyof TransactionsInterface,
      /**
       * Render updated date and time.
       * @param {TransactionsInterface} rowData - The row data.
       * @returns {React.JSX.Element} The formatted date time element.
       */
      customData: (rowData: TransactionsInterface) => (
        <span>{ConvertUTCtoDateTime(rowData.updatedAt)}</span>
      ),
    },
  ];

  /**
   * Column definitions for the table export.
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('CDR ID'), accessorKey: 'cdrId' },
    { label: t('Session ID'), accessorKey: 'sessionId' },
    { label: t('OCPPTransactionid'), accessorKey: 'transactionId' },
    { label: t('Account ID'), accessorKey: 'accountCustomId' },
    { label: t('Charging Point'), accessorKey: 'chargingPointSn' },
    { label: t('Location ID'), accessorKey: 'locationId' },
    { label: t('EVSE UID'), accessorKey: 'evseUid' },
    { label: t('connectorId'), accessorKey: 'connector' },
    { label: t('tariffName'), accessorKey: 'tariff' },
    { label: t('Energy (kWh)'), accessorKey: 'totalEnergy' },
    { label: t('Total Cost'), accessorKey: 'totalCost' },
    { label: t('Total Time'), accessorKey: 'totalTime' },
    { label: t('Created'), accessorKey: 'createdAt' },
    { label: t('Last Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns.
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Table pagination limit change function.
   * @param {any} value - The pagination parameter object.
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
    fetchData({
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      ...sortValue,
    });
  };

  /**
   * Export data function for pdf and csv.
   * @param {string} type - Export format ('csv' | 'pdf').
   */
  const exportDataPdfOrCSV = (type: string) => {
    if (!stationId) return;
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: Record<string, any> = {
      ...queryParameters,
      stationId: [stationId],
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    cpoTransactionApi
      .fetchCpoTransactionsExport(payload)
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: TransactionsInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'cdrId':
                return row.cdrId || t('na');
              case 'sessionId':
                return row.sessionId || t('na');
              case 'transactionId':
                return row.transactionId || t('na');
              case 'accountCustomId':
                return row.accountCustomId || t('na');
              case 'chargingPointSn':
                return row.chargingPointSn || t('na');
              case 'locationId':
                return row.cdrLocation?.id || '-';
              case 'evseUid':
                return row.cdrLocation?.evseUid || '-';
              case 'connector':
                return row.cdrLocation?.connectorId || '-';
              case 'tariff':
                return row.tariffs?.[0]?.name || '-';
              case 'totalEnergy':
                return ToFixedValue(row.totalEnergy) ?? '0.00';
              case 'totalCost':
                return row.totalCost?.inclVat !== undefined
                  ? `${ToFixedValue(row.totalCost.inclVat)} ${row.currency}`
                  : `0.00 ${row.currency ?? ''}`;
              case 'totalTime':
                return formatTotalTime(row.totalTime, t);
              case 'createdAt':
                return ConvertUTCtoDateTime(row.createdAt);
              case 'updatedAt':
                return ConvertUTCtoDateTime(row.updatedAt);
              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'location-transactions.csv');
          } else {
            exportPDF(headers, rows, 'location-transactions.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the sort data function.
   * @param {any} sortData - The sorting configuration.
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (
      isFilterUpdated ||
      transactions.length === 0 ||
      dateRange.startDate ||
      dateRange.endDate ||
      stationId
    ) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...queryParameters,
        sortBy,
        sortField,
      });
      dispatch(disableTransactionFiltersApplied());
    }
  }, [filters, dateRange.endDate, dateRange.startDate, stationId]);

  useEffect(() => {
    if (!stationId) return;

    const lastId = sessionStorage.getItem('TransactionsFiltersList');
    if (lastId !== stationId || filters.length === 1) {
      dispatch(fetchTransactionsFilters({ chargingStation: stationId }));
    }
    sessionStorage.setItem('TransactionsFiltersList', stationId);
  }, [stationId]);

  useEffect(() => {
    if (
      searchParameters.get('deviceId') ||
      searchParameters.get('userCustomId') ||
      searchParameters.get('transactionObjectId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('deviceId');
      parameters.delete('userCustomId');
      parameters.delete('transactionObjectId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="transaction-wrap" dir={i18n.dir()}>
      <div className="transaction-right-content">
        <CustomTable
          dataKey="Transactions"
          version="v2"
          header={column}
          data={transactions}
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          onHiddenColumnsChange={setHiddenColumns}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updateTransactionsFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchTransactionsFilters}
          refreshRequired
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          exportDataPdfOrCSV={
            permissionCheck('cpoTransactionExport') || permissionCheck('partnerTransactionExport')
              ? exportDataPdfOrCSV
              : undefined
          }
          exportLoader={exportLoader}
          hoverLabel={t('refresh')}
          rightTopContent={renderDateFilters()}
          onClearFilterChange={() => {
            clearDateRange();
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={handleSidePanelClose}
          height={600}
        />
      </div>
    </div>
  );
}

export default Transactions;
